//src/pages/AiHistoryReportPage.tsx
import { useState } from "react";
import { BrainCircuit, Printer, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PaginationFooter } from "@/components/shared/PaginationFooter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAiHistory } from "@/hooks/useAi";

function formatCurrency(value?: string | number | null, currency = "BRL") {
  const n = Number(value || 0);
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency }).format(Number.isFinite(n) ? n : 0);
}

function formatInteger(value?: string | number | null) {
  const n = Number(value || 0);
  return new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(Number.isFinite(n) ? n : 0);
}

function formatDateTime(value?: string | null) {
  if (!value) return "-";
  try {
    const date = new Date(value);
    const day = new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" }).format(date);
    const time = new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" }).format(date);
    return `${day} às ${time}`;
  } catch {
    return value;
  }
}

function formatDate(value?: string | null) {
  if (!value) return "-";
  try {
    return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" }).format(new Date(`${value}T00:00:00`));
  } catch {
    return value;
  }
}

function transactionLabel(type?: string | null) {
  const map: Record<string, string> = {
    credit_purchase: "Crédito comprado",
    manual_adjustment_credit: "Ajuste de crédito",
    manual_adjustment_debit: "Ajuste de débito",
    ai_usage_debit: "Uso Normex Copilot",
    refund: "Estorno",
  };
  return map[type || ""] || type || "-";
}

function featureLabel(feature?: string | null) {
  const map: Record<string, string> = {
    campaign_risk_action_suggestions: "Sugestões de campanha",
    inventory_executive_insights: "Insights do inventário",
  };
  return map[feature || ""] || feature || "-";
}

export default function AiHistoryReportPage() {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<10 | 20 | 30>(20);
  const [transactionType, setTransactionType] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const historyQ = useAiHistory({
    page,
    per_page: perPage,
    transaction_type: transactionType === "all" ? undefined : transactionType,
    start_date: startDate || undefined,
    end_date: endDate || undefined,
  });
  const data = historyQ.data;
  const periodLabel = startDate || endDate ? `${formatDate(startDate) || "-"} até ${formatDate(endDate) || "-"}` : "Todos os períodos";

  return (
    <div className="space-y-6 pb-10 print:space-y-4 print:bg-white print:pb-0">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between print:block">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-950">Histórico Normex Copilot</h1>
          <p className="text-sm text-muted-foreground">Extrato analítico de créditos, consumo e tokens das requests do Normex Copilot.</p>
          <p className="mt-1 hidden text-xs text-muted-foreground print:block">Período: {periodLabel}</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row print:hidden">
          <Button variant="outline" onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" />
            Imprimir / Salvar PDF
          </Button>
          <Button variant="outline" onClick={() => historyQ.refetch()} disabled={historyQ.isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${historyQ.isFetching ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        </div>
      </div>

      <Card className="p-4 print:hidden">
        <div className="grid gap-3 md:grid-cols-4">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Data inicial</label>
            <input
              type="date"
              value={startDate}
              onChange={(event) => { setStartDate(event.target.value); setPage(1); }}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Data final</label>
            <input
              type="date"
              value={endDate}
              onChange={(event) => { setEndDate(event.target.value); setPage(1); }}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Tipo de lançamento</label>
            <Select value={transactionType} onValueChange={(value) => { setTransactionType(value); setPage(1); }}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os lançamentos</SelectItem>
                <SelectItem value="credit_purchase">Créditos comprados</SelectItem>
                <SelectItem value="ai_usage_debit">Usos do Normex Copilot</SelectItem>
                <SelectItem value="refund">Estornos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => { setStartDate(""); setEndDate(""); setTransactionType("all"); setPage(1); }}
            >
              Limpar filtros
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 print:grid-cols-4 print:gap-2">
        <Card className="p-4 print:p-3">
          <div className="text-sm text-muted-foreground">Saldo atual</div>
          <div className="mt-1 text-xl font-bold">{formatCurrency(data?.balance)}</div>
        </Card>
        <Card className="p-4 print:p-3">
          <div className="text-sm text-muted-foreground">Créditos adicionados</div>
          <div className="mt-1 text-xl font-bold text-emerald-700">{formatCurrency(data?.total_credits)}</div>
        </Card>
        <Card className="p-4 print:p-3">
          <div className="text-sm text-muted-foreground">Créditos consumidos</div>
          <div className="mt-1 text-xl font-bold text-red-700">{formatCurrency(data?.total_debits)}</div>
        </Card>
        <Card className="p-4 print:p-3">
          <div className="text-sm text-muted-foreground">Tokens totais</div>
          <div className="mt-1 text-xl font-bold">{formatInteger(data?.total_tokens)}</div>
        </Card>
      </div>

      <Card className="p-4 print:border-none print:p-0 print:shadow-none">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between print:mb-2">
          <div className="flex items-center gap-2 font-semibold">
            <BrainCircuit className="h-4 w-4 text-blue-600 print:hidden" />
            Extrato de créditos Normex Copilot
          </div>
          <div className="text-xs text-muted-foreground">Período: {periodLabel}</div>
        </div>

        {historyQ.isLoading ? (
          <Card className="p-6 text-sm text-muted-foreground">Carregando histórico do Normex Copilot...</Card>
        ) : historyQ.isError ? (
          <Card className="p-6 text-sm text-destructive">{(historyQ.error as any)?.message || "Erro ao carregar histórico do Normex Copilot."}</Card>
        ) : !data?.items?.length ? (
          <Card className="p-6 text-sm text-muted-foreground">Nenhum lançamento encontrado.</Card>
        ) : (
          <div className="overflow-x-auto print:overflow-visible">
            <table className="w-full min-w-[1100px] border-collapse text-sm print:min-w-0 print:text-[10px]">
              <thead>
                <tr className="border-b bg-slate-50 text-left text-xs uppercase tracking-wide text-muted-foreground print:text-[9px]">
                  <th className="px-3 py-3 print:px-1 print:py-2">Data</th>
                  <th className="px-3 py-3 print:px-1 print:py-2">Tipo</th>
                  <th className="px-3 py-3 print:px-1 print:py-2">Funcionalidade</th>
                  <th className="px-3 py-3 print:px-1 print:py-2">Referência</th>
                  <th className="px-3 py-3 print:px-1 print:py-2">Usuário</th>
                  <th className="px-3 py-3 text-right print:px-1 print:py-2">Entrada</th>
                  <th className="px-3 py-3 text-right print:px-1 print:py-2">Saída</th>
                  <th className="px-3 py-3 text-right print:px-1 print:py-2">Total</th>
                  <th className="px-3 py-3 text-right print:px-1 print:py-2">Valor</th>
                  <th className="px-3 py-3 text-right print:px-1 print:py-2">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.id} className="border-b last:border-b-0">
                    <td className="px-3 py-3 whitespace-nowrap print:px-1 print:py-2">{formatDateTime(item.created_at)}</td>
                    <td className="px-3 py-3 whitespace-nowrap print:px-1 print:py-2">{transactionLabel(item.transaction_type)}</td>
                    <td className="px-3 py-3 print:px-1 print:py-2">{featureLabel(item.feature)}</td>
                    <td className="px-3 py-3 print:px-1 print:py-2">
                      <div className="font-medium">{item.campaign_description || item.campaign_title || item.inventory_title || item.reference_type || "-"}</div>
                    </td>
                    <td className="px-3 py-3 print:px-1 print:py-2">{item.created_by_name || item.created_by || "-"}</td>
                    <td className="px-3 py-3 text-right print:px-1 print:py-2">{item.input_tokens !== null && item.input_tokens !== undefined ? formatInteger(item.input_tokens) : "-"}</td>
                    <td className="px-3 py-3 text-right print:px-1 print:py-2">{item.output_tokens !== null && item.output_tokens !== undefined ? formatInteger(item.output_tokens) : "-"}</td>
                    <td className="px-3 py-3 text-right print:px-1 print:py-2">{item.total_tokens !== null && item.total_tokens !== undefined ? formatInteger(item.total_tokens) : "-"}</td>
                    <td className={`px-3 py-3 text-right font-medium print:px-1 print:py-2 ${Number(item.amount || 0) < 0 ? "text-red-700" : "text-emerald-700"}`}>{formatCurrency(item.amount)}</td>
                    <td className="px-3 py-3 text-right font-medium print:px-1 print:py-2">{formatCurrency(item.balance_after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data ? (
          <div className="mt-4 border-t pt-4 print:hidden">
            <PaginationFooter
              page={data.page}
              perPage={data.per_page as 10 | 20 | 30}
              total={data.total}
              totalPages={Math.max(Math.ceil(data.total / data.per_page), 1)}
              onPageChange={setPage}
              onPerPageChange={(value) => { setPerPage(value); setPage(1); }}
              label="lançamentos"
            />
          </div>
        ) : null}
      </Card>
    </div>
  );
}
